/**
 * /admin/questions — the bank.
 *
 * A dense list on the left, the selected question rendered exactly as the
 * student sees it on the right. The review queue is a separate screen because
 * it is a different job: this one is for finding a question, that one is for
 * getting through a backlog.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import type { QuestionWithContext } from "@exam/shared";
import { useCatalog } from ".";
import {
  EMPTY_FILTERS,
  QuestionFilters,
  type QuestionFilterValues,
  toQuery,
} from "./components/QuestionFilters";
import { QuestionMeta } from "./components/QuestionMeta";
import { QuestionView } from "./components/QuestionView";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import { fetchQuestions, fetchReviewQueue } from "./data/client";
import { useAsync } from "./lib/useAsync";

const PAGE_SIZE = 50;

export function QuestionsPage() {
  const catalog = useCatalog();
  const [filters, setFilters] = useState<QuestionFilterValues>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<QuestionWithContext | null>(null);

  const query = toQuery(filters);
  const state = useAsync(
    () => fetchQuestions({ ...query, limit: PAGE_SIZE, offset }),
    [JSON.stringify(query), offset],
  );

  // Only to show how much is waiting — the queue itself lives on its own route.
  const pending = useAsync(() => fetchReviewQueue(1, 0), []);
  const pendingCount = pending.data?.total ?? 0;

  const questions = state.data?.questions ?? [];
  const total = state.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <PageTitle
        title="Question bank"
        meta={`${total} question${total === 1 ? "" : "s"} match the current filters.`}
        actions={
          <Link to="/admin/questions/review">
            <Button variant="primary" icon={<ClipboardCheck />}>
              Review queue
              {pendingCount > 0 ? ` (${pendingCount})` : ""}
            </Button>
          </Link>
        }
      />

      <FixtureBanner />

      <Card>
        <div className="border-b border-border px-4 py-3">
          <QuestionFilters
            catalog={catalog}
            values={filters}
            onChange={(next) => {
              setFilters(next);
              setOffset(0);
            }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
          <div className="min-w-0 border-b border-border lg:border-b-0 lg:border-r">
            {state.loading ? (
              <Loading />
            ) : state.error ? (
              <div className="p-4">
                <ErrorNote error={state.error} />
              </div>
            ) : (
              <>
                <Table>
                  <THead>
                    <tr>
                      <TH>Question</TH>
                      <TH>Topic</TH>
                      <TH align="right">Wk</TH>
                      <TH>Type</TH>
                      <TH>Status</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {questions.length === 0 ? (
                      <TableEmpty colSpan={5}>
                        No questions match these filters.
                      </TableEmpty>
                    ) : (
                      questions.map((question) => (
                        <TR
                          key={question.id}
                          selected={selected?.id === question.id}
                          onClick={() => setSelected(question)}
                          className="cursor-pointer"
                        >
                          <TD className="max-w-0 w-full">
                            <span className="block truncate">
                              {plainSummary(question.body_md)}
                            </span>
                          </TD>
                          <TD className="max-w-36 text-ink-muted">
                            <span className="block truncate">{question.topic_name}</span>
                          </TD>
                          <TD align="right" className="tabular">
                            {question.week_no ?? "—"}
                          </TD>
                          <TD>{question.type}</TD>
                          <TD>
                            <Badge
                              tone={
                                question.status === "approved"
                                  ? "answered"
                                  : question.status === "pending"
                                    ? "review"
                                    : "violation"
                              }
                            >
                              {question.status}
                            </Badge>
                          </TD>
                        </TR>
                      ))
                    )}
                  </TBody>
                </Table>

                {total > PAGE_SIZE ? (
                  <div className="flex items-center justify-between border-t border-border px-4 py-3">
                    <p className="tabular text-2xs text-ink-faint">
                      {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        disabled={offset + PAGE_SIZE >= total}
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="min-w-0">
            {selected ? (
              <div className="space-y-3 p-4">
                <QuestionMeta question={selected} />
                <QuestionView question={selected} showAnswer />
              </div>
            ) : (
              <p className="p-4 text-xs text-ink-faint">
                Select a question to see it as the student would.
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

/** First line of a body, stripped of markdown and math, for the list column. */
export function plainSummary(bodyMd: string): string {
  const firstLine =
    bodyMd
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l.length > 0) ?? "";
  return (
    firstLine
      // Unwrap math delimiters, then drop the TeX scaffolding inside.
      .replace(/\$\$?([^$]*)\$\$?/g, "$1")
      .replace(/\\(begin|end)\{[a-zA-Z*]+\}/g, " ")
      .replace(/\\[a-zA-Z]+/g, " ")
      .replace(/[{}&]/g, " ")
      .replace(/\\\\/g, " ")
      .replace(/[*_`#>]/g, "")
      .replace(/\s+/g, " ")
      .trim()
  );
}
